import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Crown, Sparkles, UserPlus } from "lucide-react";
import { useI18n } from "../i18n";

// Listens to window "goldora:quota-blocked" events dispatched by any gated call.
// Payload shape: { feature, limit, used, plan, error, message_pt, message_en, upgrade_url }
export default function QuotaModal() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [data, setData] = useState(null);

  useEffect(() => {
    const handler = (e) => setData(e.detail || {});
    window.addEventListener("goldora:quota-blocked", handler);
    return () => window.removeEventListener("goldora:quota-blocked", handler);
  }, []);

  if (!data) return null;

  const isRegistration = data.error === "registration_required";
  const feature = data.feature || "feature";

  const featureLabels = {
    chat: pt ? "perguntas à IA" : "AI questions",
    tts: pt ? "respostas em áudio (TTS)" : "voice answers (TTS)",
    transcribe: pt ? "transcrições por voz" : "voice transcriptions",
    comparator: pt ? "comparações cruzadas" : "cross-asset comparisons",
    simulator: pt ? "simulações" : "simulations",
  };

  const title = isRegistration
    ? (pt ? "Faça seu cadastro gratuito" : "Create your free account")
    : (pt ? "Você bateu seu limite diário gratuito" : "You hit your free daily limit");

  const desc = isRegistration
    ? (pt
        ? `Para usar ${featureLabels[feature] || feature} você precisa de uma conta gratuita. Só pedimos nome e e-mail — nada de patrimônio, CPF ou cartão.`
        : `To use ${featureLabels[feature] || feature} you need a free account. We only ask for name and email — no net-worth, no ID, no card.`)
    : ((pt ? data.message_pt : data.message_en) ||
        (pt ? "Assine Pro para uso ilimitado." : "Upgrade to Pro for unlimited use."));

  const close = () => setData(null);

  const openRegister = () => {
    close();
    window.dispatchEvent(new CustomEvent("goldora:open-register"));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/80 backdrop-blur-md" data-testid="quota-modal">
      <div className="surface-elevated max-w-md w-full p-7 relative rounded-xl border border-gold-500/30 shadow-2xl">
        <button
          onClick={close}
          aria-label="Close"
          data-testid="quota-modal-close"
          className="absolute top-3 right-3 p-1.5 text-neutral-500 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
        <div className="flex items-center gap-2 text-gold-400">
          {isRegistration ? <UserPlus size={16} /> : <Crown size={16} />}
          <span className="text-xs uppercase tracking-[0.25em] font-mono">
            {isRegistration
              ? (pt ? "Conta gratuita" : "Free account")
              : (pt ? "Plano Explorer gratuito" : "Free Explorer plan")}
          </span>
        </div>
        <h3 className="mt-3 font-display text-2xl tracking-tight">{title}</h3>
        <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{desc}</p>

        {!isRegistration && data.limit && (
          <div className="mt-4 text-xs font-mono text-neutral-500">
            <span className="text-gold-400">{featureLabels[feature] || feature}</span>
            {" · "}
            {pt ? "limite gratuito" : "free limit"}: {data.used}/{data.limit} {pt ? "hoje" : "today"}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-2.5">
          {isRegistration ? (
            <>
              <button
                type="button"
                onClick={openRegister}
                data-testid="quota-modal-register"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-5 py-3 rounded-md transition-colors"
              >
                <UserPlus size={14} />
                {pt ? "Cadastrar gratuitamente" : "Sign up for free"}
              </button>
              <Link
                to="/planos"
                onClick={close}
                data-testid="quota-modal-see-plans"
                className="text-xs font-mono uppercase tracking-widest text-gold-400/80 hover:text-gold-300 py-2 text-center"
              >
                {pt ? "ou ver planos pagos" : "or see paid plans"}
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/planos"
                onClick={close}
                data-testid="quota-modal-upgrade"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-5 py-3 rounded-md transition-colors"
              >
                <Sparkles size={14} />
                {pt ? "Ver planos Pro e Elite" : "See Pro and Elite plans"}
              </Link>
              <button
                onClick={close}
                data-testid="quota-modal-dismiss"
                className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-300 py-2"
              >
                {pt ? "Lembrar mais tarde" : "Remind me later"}
              </button>
            </>
          )}
        </div>

        <p className="mt-5 text-[11px] text-neutral-600 leading-relaxed">
          {isRegistration
            ? (pt
                ? "Sem cartão de crédito. Sem cobrança automática. Você pode cancelar a conta a qualquer momento."
                : "No credit card. No auto-billing. You can delete your account at any time.")
            : (pt
                ? "Limites diários são resetados a cada 24h. Sem pegadinhas — você pode cancelar a qualquer momento."
                : "Daily limits reset every 24h. No gotchas — cancel anytime.")}
        </p>
      </div>
    </div>
  );
}

// Helper dispatcher so other components can trigger the modal programmatically.
export function triggerQuotaModal(detail) {
  window.dispatchEvent(new CustomEvent("goldora:quota-blocked", { detail }));
}
